import * as React from 'react'
import { SupportedRuntimesTooltipResolver } from '../../util/SupportedRuntimesHelper'
import { Environment } from '../../remote-api/CoreServicesRemoteApi'
import { getResolvedFileSize, isGreaterThanDownloadLimit } from '../../util/FileSizeHelper'
import {
  getRawDataResourceTypeFromResource,
  LogFile,
  RawDataResourceType
} from './model'
import { EmptyStateSvg } from './EmptyStateSvg'
import RawDataPageSizeCell from './RawDataPageSizeCell'
import FixedTable from '@mulesoft/anypoint-components/lib/FixedTable'
import TextField from '@mulesoft/anypoint-components/lib/TextField'
import Spinner from '@mulesoft/anypoint-components/lib/Spinner'
import Button from '@mulesoft/anypoint-components/lib/Button'
import Select from '@mulesoft/anypoint-components/lib/Select'
import './BasicRawData.css'
import './RawDataPage.css'
import { EnvironmentSelectorState } from '../../common/environment/reducer'
import { ResourceSelectorState } from '../../common/resource/reducer'
import {
  ResourceType
} from '@mulesoft/monitoring-center-components/lib/models/resource'
import { ResourceTargetSelectorState } from '../../common/resource/target/reducer';
import {
  getResourceId,
  Resource,
  transformToResourceSelectorProps
} from '../../common/resource/model';
import { BasicTarget } from '../../common/resource/target/model';
import { transformToEnvironmentSelectorProps } from '../../common/environment/model';
import MonitoringSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/MonitoringSelector/MonitoringSelector';

export type ExternalProps = {
  environmentId: string
  resourceType: RawDataResourceType
  resourceId: string
  date: string
}

export type ValueProps = {
  isLoading: boolean
  organizationId: string
  authorization: string
  logFiles: LogFile[]
  environmentSelector: EnvironmentSelectorState
  resourceSelector: ResourceSelectorState
  targetsSelector: ResourceTargetSelectorState
}

export type EventProps = {
  load: (props: ExternalProps) => void
  onView: (props: ExternalProps) => void
  downloadLogFile: (fileKey: string) => void
  environmentSelected: (environmentId: string, environments?: Environment[]) => void
  resourceSelected: (resourceId: string, resourceType: ResourceType, resources?: Resource[]) => void
  changeSelectedDate: (selectedDate: string) => void
  onTargetsChange: (targets: BasicTarget[]) => void
}

export type Props = ValueProps & ExternalProps

export type AllProps = Props & EventProps

export type State = {
  downloadedFiles: Set<string>
  date: string
}

export class RawDataPage extends React.PureComponent<AllProps, State> {

  private columns = [
    {
      key: 'fileName',
      name: 'File name',
      width: 800
    },
    {
      key: 'workerId',
      name: 'Target',
      renderer: (row: LogFile) => {
        const workerId = row.workerId.toString()
        const {targetsSelector} = this.props

        const worker = targetsSelector.resources ? targetsSelector.resources.filter(w => w.id === workerId) : []

        return <span>{worker.length > 0 ? worker[0].name : workerId}</span>
      }
    },
    {
      key: 'size',
      name: 'Size',
      width: 175,
      disableSort: true,
      renderer: (row: LogFile) => {
        const fileName = row.fileName
        const fileUrl = row.fileUrl
        const token = this.props.authorization
        const size = getResolvedFileSize(row.size)
        const isDisabled= isGreaterThanDownloadLimit(row.size)
        const isVisited = this.state.downloadedFiles.has(row.fileKey)
        const onDownload = () => {
          const callback = () => this.onDownloadLogFile(fileName)
          const newStatePick = {downloadedFiles: this.state.downloadedFiles.add(row.fileKey)}
          this.setState(newStatePick, callback)
        }

        const props = {size, fileUrl, fileName, token, isVisited, isDisabled, onDownload}
        return <RawDataPageSizeCell {...props}/>
      }
    }
  ]

  constructor(props: AllProps) {
    super(props)

    this.state = {
      downloadedFiles: new Set<string>(),
      date: props.date
    }

    this.getMonitoringSelectorProps = this.getMonitoringSelectorProps.bind(this)
    this.getTargetSelectorProps = this.getTargetSelectorProps.bind(this)
    this.getViewButtonProps = this.getViewButtonProps.bind(this)

    this.renderErrorState = this.renderErrorState.bind(this)
    this.onDateChange = this.onDateChange.bind(this)
    this.environmentSelected = this.environmentSelected.bind(this)
    this.resourceSelected = this.resourceSelected.bind(this)
    this.onTargetsChange = this.onTargetsChange.bind(this)
    this.clickOnView = this.clickOnView.bind(this)
    this.onDownloadLogFile = this.onDownloadLogFile.bind(this)

  }

  getMonitoringSelectorProps() {
    const {environmentSelector, resourceSelector} = this.props

    return {
      className: 'raw-data-page-selector-container',
      environmentSelector: transformToEnvironmentSelectorProps(environmentSelector, 25, 'raw-data-environment-selector'),
      resourceSelector: transformToResourceSelectorProps(resourceSelector, 40, 'raw-data-resource-selector'),
      versionSelector: {isLoading: false, maxLength: 35},
      environmentSelected: this.environmentSelected,
      resourceSelected: this.resourceSelected,
      versionSelected: (_: string) => undefined
    }
  }

  getTargetSelectorProps() {
    const {targetsSelector} = this.props
    let options
    let value

    if (targetsSelector.isFetching || !targetsSelector.resources) {
      options = [{value: '', label: 'Loading...'}]
      value = {value: '', label: 'Loading...'}
    } else {
      options = targetsSelector.resources.map(t => ({value: t.id, label: t.name}))
      value = targetsSelector.selectedResources ? targetsSelector.selectedResources.map(t => ({value: t.id, label: t.name})) : []
    }

    return {
      multi: true,
      clearable: true,
      customInput: /.+@.+\..+/i,
      className: 'workers-select',
      testId: 'workers-select',
      name: 'targets',
      onChange: this.onTargetsChange,
      searchable: true,
      options: options,
      value: value,
      placeholder: 'Select the targets...',
      disabled: targetsSelector.isFetching || !targetsSelector.resources
    }
  }

  onDownloadLogFile(fileName: string) {
    this.props.downloadLogFile(fileName)
  }

  onTargetsChange(result: any) {
    const {targetsSelector} = this.props

    const value = result.value
    const targetIds = value ? value.split(',') : []
    const targets = targetsSelector.resources && targetsSelector.resources.filter(t => targetIds.includes(t.id))

    this.props.onTargetsChange(targets || [])
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, prevContext: any): void {
    const {environmentId, resourceId, resourceType, date} = this.props
    const newEnvironmentId = prevProps.environmentId !== environmentId
    const newResourceId = prevProps.resourceId !== resourceId
    const newResourceType = prevProps.resourceType !== resourceType
    const newDate = prevProps.date !== date

    if (newEnvironmentId || newResourceId || newResourceType || newDate) {
      this.props.load(this.props)
    }
  }

  componentWillMount() {
    this.props.load(this.props)
  }

  environmentSelected(environmentId: string) {
    const {environmentSelected, environmentSelector} = this.props

    environmentSelected(environmentId, environmentSelector.resources)
  }

  resourceSelected(resourceId: string, resourceType: ResourceType) {
    const {resourceSelected, resourceSelector} = this.props

    resourceSelected(resourceId, resourceType, resourceSelector.resources)
  }

  onDateChange(e: { value: string }) {
    this.setState({...this.state, date: e.value})
  }

  clickOnView() {
    const {environmentSelector, resourceSelector} = this.props
    const {date} = this.state

    const environmentId = environmentSelector.selected && environmentSelector.selected.id
    const resourceId = resourceSelector.selected && getResourceId(resourceSelector.selected)
    const resourceType = resourceSelector.selected && getRawDataResourceTypeFromResource(resourceSelector.selected)

    if (environmentId && resourceId && resourceType && date) {
      this.props.onView({environmentId, resourceType, resourceId, date})
    }
  }

  getViewButtonProps() {
    const {environmentId, resourceId} = this.props
    const {date} = this.state
    const isDisabled = !(environmentId && resourceId && date)

    return {
      className: 'view-raw-data-button',
      kind: 'primary' as 'primary',
      testId: 'view-raw-data-button',
      onClick: this.clickOnView,
      disabled: isDisabled
    }
  }

  render() {
    const {
      isLoading,
      logFiles
    } = this.props

    const {date} = this.state

    const monitoringSelectorProps = this.getMonitoringSelectorProps()
    const targetSelectorProps = this.getTargetSelectorProps()
    const viewButtonProps = this.getViewButtonProps()

    return (
      <div className={'raw-data-page-container'}>
        <div className={'raw-data-page-header'}>
          <div className={'raw-data-page-header-selector'}>
            <MonitoringSelector {...monitoringSelectorProps}/>
            <div className={'raw-data-date-picker'}>
              <div className={'select-input'}>
                <div data-test-id={'date-label'} className={'select-label'}>Date (UTC): mm/dd/yyyy</div>
                <TextField
                    className={'date-form-input'}
                    testId={'date-form-input'}
                    name="date"
                    type="text"
                    onChange={this.onDateChange}
                    value={date}
                />
              </div>
            </div>
            <Button {...viewButtonProps}>View</Button>
          </div>
        </div>
        <div className={'raw-data-body-container'}>
          <div className={'raw-data-container'}>
            <div className={'worker-selector-container'}>
              <div data-test-id={'workers-label'} className={'worker-selector-title'}>Filter files by target:</div>
              <Select {...targetSelectorProps}/>
            </div>
            {!isLoading && logFiles && logFiles.length > 0 &&
              <div className={logFiles.length > 9 ? 'raw-data-list raw-data-list-extended' : 'raw-data-list'}>
                <FixedTable testId={'log-files-table'} columns={this.columns} data={logFiles}/>
              </div>
            }
            {!isLoading && (!logFiles || logFiles.length === 0) &&
                this.renderErrorState()
            }
            {isLoading &&
                RawDataPage.renderLoadingState()
            }
          </div>
        </div>
      </div>
    )
  }

  static renderLoadingState() {
    return (
      <div className={'loading-state-container'}>
        <span data-test-id={'loading-state-title'} className={'loading-state-title'}>Loading...</span>
        <Spinner/>
      </div>
    )
  }

  renderErrorState() {
    const {targetsSelector} = this.props
    const targets = targetsSelector.resources

    return (
      <div className={'error-state-container'}>
        <span data-test-id={'error-state-title'} className={'error-state-title'}>
          {(!targets || targets.length <= 0) &&
            'No available targets. Application may be disconnected or restarting'
          }
          {(targets && targets.length > 0 && (!targetsSelector.selectedResources || targetsSelector.selectedResources.length <= 0)) &&
            'Please select a target from filtration menu.'
          }
          {(targetsSelector.selectedResources && targetsSelector.selectedResources.length > 0) &&
              'No data found for the selected date. Try a different date.'
          }
        </span>
        <SupportedRuntimesTooltipResolver/>
        <EmptyStateSvg/>
      </div>
    )
  }
}
